// Parity P5 wave 1 (hybrid design §6.1 automations, §6.2 recurring work): the rows and the
// automation spec. A rule is "When <trigger> If <conditions> Then <actions>", typed: every
// picker in the builder is one of these fields, and the server refuses a rule whose parts do not
// fit together (an action that needs a task under a trigger that has none, a board-scoped rule
// naming another board's status). Wave 2 adds the playbook, flag and form triggers and actions.
import { z } from "zod";
import { ActorShortColumns } from "./actor";
import {
  AutomationPauseReason,
  AutomationRunOutcome,
  AutomationScope,
  AutomationSource,
  RecurringRunOutcome,
  RecurringTakeoverScope,
  TaskLinkRelation,
} from "./enums";
import { Decimal, LIMITS, Timestamp, text, Uuid } from "./primitives";

const actor = ActorShortColumns.shape;
const stamps = { created_at: Timestamp, updated_at: Timestamp };

const StatusKey = text(LIMITS.key);
const Priority = z.int().min(0).max(4);
/** `HH:MM`, 24-hour, in the company's calendar time zone. */
const ClockTime = z
  .string()
  .max(5)
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/);

// Triggers ----------------------------------------------------------------------------------------

/** A change to one of a task's files (`task.file_changed`'s `op`). */
export const TaskFileOp = z.enum([
  "added",
  "renamed",
  "moved",
  "cover_set",
  "cover_cleared",
  "trashed",
  "restored",
]);

/**
 * When (design §6.1). Task triggers carry the task as the rule's subject; `schedule`,
 * `message.posted` and `observation.recorded` carry none (only `create_task` and `wake_agent`
 * act under them).
 */
export const AutomationTrigger = z
  .discriminatedUnion("type", [
    z.strictObject({ type: z.literal("task.created") }),
    z.strictObject({ type: z.literal("task.moved") }),
    z.strictObject({
      type: z.literal("task.status_changed"),
      /** Only when it becomes this status (absent: any change). */
      to: StatusKey.optional(),
    }),
    z.strictObject({ type: z.literal("task.field_changed"), field_id: Uuid }),
    z.strictObject({ type: z.literal("task.completed") }),
    z.strictObject({ type: z.literal("task.tagged"), tag_id: Uuid.optional() }),
    z.strictObject({ type: z.literal("task.commented") }),
    z.strictObject({ type: z.literal("task.due_soon"), hours: z.int().min(1).max(168) }),
    z.strictObject({ type: z.literal("task.overdue") }),
    z.strictObject({
      type: z.literal("schedule"),
      /** Daily at this time, in the company's time zone. */
      at: ClockTime,
      /** ISO weekdays it runs on (1 Monday … 7 Sunday); absent: every day. */
      weekdays: z.array(z.int().min(1).max(7)).min(1).max(7).optional(),
    }),
    z.strictObject({ type: z.literal("message.posted"), channel_id: Uuid }),
    z.strictObject({ type: z.literal("observation.recorded"), metric_def_id: Uuid }),
    // B1-DE-RULES (universal-task plan §b.1): fixed-meaning triggers a DE writes rules on
    z.strictObject({
      type: z.literal("date_field_reached"),
      /** A date field of the board. */
      field_id: Uuid,
      /** Hours from the date: negative before it ("2 days before"), positive after; 0 at it. */
      offset_hours: z.int().min(-2160).max(2160),
    }),
    z.strictObject({
      type: z.literal("task.file_changed"),
      /** Only this change to a file (absent: any). */
      op: TaskFileOp.optional(),
    }),
    z.strictObject({
      type: z.literal("task.linked"),
      /** Only a link of this kind (absent: any). */
      relation: TaskLinkRelation.optional(),
    }),
  ])
  .meta({ id: "AutomationTrigger" });
export type AutomationTrigger = z.infer<typeof AutomationTrigger>;

/** The triggers whose subject is a task. */
export const TASK_TRIGGERS = [
  "task.created",
  "task.moved",
  "task.status_changed",
  "task.field_changed",
  "task.completed",
  "task.tagged",
  "task.commented",
  "task.due_soon",
  "task.overdue",
  "date_field_reached",
  "task.file_changed",
  "task.linked",
] as const satisfies readonly AutomationTrigger["type"][];

export const isTaskTrigger = (t: AutomationTrigger["type"]): boolean =>
  (TASK_TRIGGERS as readonly string[]).includes(t);

// Conditions --------------------------------------------------------------------------------------

/** If (design §6.1): all must hold. The metric conditions read the recorded observation. */
export const AutomationCondition = z
  .discriminatedUnion("type", [
    z.strictObject({ type: z.literal("status_is"), statuses: z.array(StatusKey).min(1).max(20) }),
    z.strictObject({ type: z.literal("priority_is"), priorities: z.array(Priority).min(1).max(5) }),
    z.strictObject({ type: z.literal("has_tag"), tag_id: Uuid }),
    z.strictObject({ type: z.literal("title_contains"), text: text(LIMITS.name) }),
    z.strictObject({
      type: z.literal("field_equals"),
      field_id: Uuid,
      value: z.union([z.string().max(LIMITS.text), z.number(), z.boolean(), z.null()]),
    }),
    z.strictObject({ type: z.literal("metric_above"), value: Decimal }),
    z.strictObject({ type: z.literal("metric_below"), value: Decimal }),
  ])
  .meta({ id: "AutomationCondition" });
export type AutomationCondition = z.infer<typeof AutomationCondition>;

/** Conditions that read the task (the rule needs a task trigger). */
export const TASK_CONDITIONS = [
  "status_is",
  "priority_is",
  "has_tag",
  "title_contains",
  "field_equals",
] as const satisfies readonly AutomationCondition["type"][];

// Actions -----------------------------------------------------------------------------------------

/** Then (design §6.1), applied in order, each as the rule's owner would apply it. */
export const AutomationAction = z
  .discriminatedUnion("type", [
    z.strictObject({ type: z.literal("set_status"), status: StatusKey }),
    z.strictObject({ type: z.literal("set_priority"), priority: Priority }),
    z.strictObject({ type: z.literal("add_tag"), tag_id: Uuid }),
    z.strictObject({ type: z.literal("add_comment"), text: text(LIMITS.text) }),
    z.strictObject({ type: z.literal("assign"), seat_id: Uuid }),
    z.strictObject({
      type: z.literal("wake_agent"),
      seat_id: Uuid,
      brief: text(LIMITS.text),
    }),
    z.strictObject({
      type: z.literal("create_task"),
      board_id: Uuid,
      title: text(LIMITS.name),
      assignee_seat_id: Uuid.optional(),
    }),
    z.strictObject({
      type: z.literal("move_to_board"),
      board_id: Uuid,
      status: StatusKey.optional(),
    }),
    // B1-DE-RULES: wake the DE that owns the rule (else the board's DE owner) with the brief and
    // what set the rule off (the event, its task, what changed)
    z.strictObject({ type: z.literal("wake_owner"), brief: text(LIMITS.text) }),
  ])
  .meta({ id: "AutomationAction" });
export type AutomationAction = z.infer<typeof AutomationAction>;

/** Actions that change the triggering task (the rule needs a task trigger). */
export const TASK_ACTIONS = [
  "set_status",
  "set_priority",
  "add_tag",
  "add_comment",
  "assign",
  "move_to_board",
] as const satisfies readonly AutomationAction["type"][];

/** A rule's definition: what the builder edits and an `automation` proposal's `spec` carries. */
export const AutomationSpec = z
  .strictObject({
    name: text(LIMITS.name),
    scope: AutomationScope,
    /** The board of a board-scoped rule (null for company-wide). */
    board_id: Uuid.nullable(),
    trigger: AutomationTrigger,
    conditions: z.array(AutomationCondition).max(10),
    actions: z.array(AutomationAction).min(1).max(10),
    /** Effects per hour before the rule pauses itself (design: 200, configurable). */
    effect_budget_per_hour: z.int().min(1).max(10_000).optional(),
  })
  .meta({ id: "AutomationSpec" });
export type AutomationSpec = z.infer<typeof AutomationSpec>;

// Rows --------------------------------------------------------------------------------------------

export const Automation = z
  .object({
    company_id: Uuid,
    id: Uuid,
    scope: AutomationScope,
    board_id: Uuid.nullable(),
    name: z.string(),
    trigger: AutomationTrigger,
    conditions: z.array(AutomationCondition),
    actions: z.array(AutomationAction),
    enabled: z.boolean(),
    /**
     * The person accountable for it: it runs on their behalf (permissions re-checked at fire
     * time) unless a DE owns it; for a DE's rule, the person who approved it.
     */
    owner_membership_id: Uuid,
    /**
     * B1-DE-RULES: the DE seat that owns the rule (a board's rule the board's owning DE wrote): it
     * runs as the DE holding the seat, its access and policy re-checked at every firing.
     */
    owner_seat_id: Uuid.nullable(),
    source: AutomationSource,
    /** The approval that created it (an agent's proposal), if any. */
    approval_id: Uuid.nullable(),
    /** The agent seat that proposed it, if any. */
    proposed_by_seat_id: Uuid.nullable(),
    effect_budget_per_hour: z.int(),
    paused_reason: AutomationPauseReason.nullable(),
    paused_at: Timestamp.nullable(),
    position: z.int(),
    version: z.int().min(1),
    fired_count: z.int().min(0),
    last_fired_at: Timestamp.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "Automation" });
export type Automation = z.infer<typeof Automation>;

/** One action's outcome in a firing (`automation_run.effects`). */
export const AutomationEffect = z
  .strictObject({
    action: z.string(),
    ok: z.boolean(),
    /** Why it was refused or failed (the problem code or refusal), when it did not apply. */
    code: z.string().optional(),
    detail: z.string().optional(),
    /** Retried once after a failure (design: partial failure logged with one retry). */
    retried: z.boolean().optional(),
    /** The task it created, when it created one. */
    task_id: Uuid.optional(),
  })
  .meta({ id: "AutomationEffect" });
export type AutomationEffect = z.infer<typeof AutomationEffect>;

export const AutomationRun = z
  .object({
    company_id: Uuid,
    id: Uuid,
    automation_id: Uuid,
    idempotency_key: z.string(),
    event_id: Uuid.nullable(),
    event_type: z.string().nullable(),
    subject_kind: z.string().nullable(),
    subject_id: Uuid.nullable(),
    depth: z.int().min(0),
    outcome: AutomationRunOutcome,
    refused_reason: z.string().nullable(),
    effects: z.array(AutomationEffect),
    effect_count: z.int().min(0),
    /** B1-DE-RULES: whom the firing acted as (the owning DE, or the owner person); null: none. */
    acted_as_membership_id: Uuid.nullable(),
    created_at: Timestamp,
  })
  .meta({ id: "AutomationRun" });
export type AutomationRun = z.infer<typeof AutomationRun>;

export const RecurringControl = z
  .object({
    company_id: Uuid,
    recurring_work_id: Uuid,
    paused_at: Timestamp.nullable(),
    taken_over_by_seat_id: Uuid.nullable(),
    takeover_scope: RecurringTakeoverScope.nullable(),
    /** "After completion": the next task this many days after the last one is done. */
    after_completion_days: z.int().nullable(),
    /** The latest occurrence's task (its completion schedules an "after completion" series). */
    last_task_id: Uuid.nullable(),
    ...actor,
    ...stamps,
  })
  .meta({ id: "RecurringControl" });
export type RecurringControl = z.infer<typeof RecurringControl>;

export const RecurringRun = z
  .object({
    company_id: Uuid,
    id: Uuid,
    recurring_work_id: Uuid,
    outcome: RecurringRunOutcome,
    scheduled_for: Timestamp.nullable(),
    period_key: z.string().nullable(),
    task_id: Uuid.nullable(),
    seat_id: Uuid.nullable(),
    trigger: z.string().nullable(),
    ...actor,
    created_at: Timestamp,
  })
  .meta({ id: "RecurringRun" });
export type RecurringRun = z.infer<typeof RecurringRun>;
